import type { Question } from '../../types';

// ---------------------------------------------------------------------------
// Property — Line of Coverage Supplement
// Source: Centered Partners MHMF intake — "Property Schedule" sheet (COPE &
// underwriting detail, one row per building).
// Gated on the Property line being selected in the coverage selector.
// ---------------------------------------------------------------------------

const PROPERTY_CONDITION = [
  { field: 'coverage_lines', operator: 'contains' as const, value: 'property' },
];

export const propertyLobQuestions: Question[] = [
  {
    id: 'prop_schedule',
    text: 'Property schedule — COPE detail per building',
    subtext:
      'One entry per building / address. Values should reflect replacement cost (RCV), not assessed or market value.',
    section: 'property',
    group: 'property_schedule',
    sort_order: 500,
    field_type: 'repeating_group',
    required: true,
    field_path: 'property.schedule',
    conditions: PROPERTY_CONDITION,
    repeating_group: {
      min_rows: 1,
      max_rows: 100,
      add_button_label: 'Add another building',
      row_label: 'Building',
      fields: [
        {
          id: 'prop_b_name',
          text: 'Property / building name',
          section: 'property',
          sort_order: 10,
          field_type: 'text',
          placeholder: 'Maple Court — Building A',
          required: true,
          field_path: 'building_name',
          conditions: [],
        },
        {
          id: 'prop_b_address',
          text: 'Address',
          section: 'property',
          sort_order: 20,
          field_type: 'text',
          placeholder: '123 Main St, St. Louis, MO 63101',
          required: true,
          field_path: 'address',
          conditions: [],
        },
        {
          id: 'prop_b_year_built',
          text: 'Year built',
          section: 'property',
          sort_order: 30,
          field_type: 'number',
          placeholder: '1998',
          required: true,
          field_path: 'year_built',
          conditions: [],
        },
        {
          id: 'prop_b_stories',
          text: '# Stories',
          section: 'property',
          sort_order: 40,
          field_type: 'number',
          placeholder: '2',
          required: true,
          field_path: 'stories',
          conditions: [],
        },
        {
          id: 'prop_b_construction',
          text: 'Construction class',
          section: 'property',
          sort_order: 50,
          field_type: 'select',
          options: [
            {
              label: 'Class 1 — Frame',
              value: '1',
              help: 'Wood frame, combustible exterior',
            },
            {
              label: 'Class 2 — Joisted Masonry',
              value: '2',
              help: 'Masonry exterior, wood roof/floor',
            },
            {
              label: 'Class 3 — Non-Combustible',
              value: '3',
              help: 'Metal/concrete exterior, combustible interior elements',
            },
            {
              label: 'Class 4 — Masonry Non-Combustible',
              value: '4',
              help: 'Masonry walls + non-combustible floor/roof',
            },
            {
              label: 'Class 5 — Modified Fire Resistive',
              value: '5',
              help: '1–2 hr fire-rated construction',
            },
            {
              label: 'Class 6 — Fire Resistive',
              value: '6',
              help: '2+ hr fire-rated, common in newer high-rises',
            },
          ],
          required: true,
          field_path: 'construction_class',
          conditions: [],
        },
        {
          id: 'prop_b_units',
          text: '# Units',
          section: 'property',
          sort_order: 60,
          field_type: 'number',
          placeholder: '24',
          required: true,
          field_path: 'units',
          conditions: [],
        },
        {
          id: 'prop_b_sf',
          text: 'Total square feet',
          section: 'property',
          sort_order: 70,
          field_type: 'number',
          placeholder: '28000',
          required: true,
          field_path: 'total_sf',
          conditions: [],
        },
        {
          id: 'prop_b_roof',
          text: 'Roof type / age',
          section: 'property',
          sort_order: 80,
          field_type: 'text',
          placeholder: 'Asphalt shingle, replaced 2019',
          required: true,
          field_path: 'roof_type_age',
          conditions: [],
        },
        {
          id: 'prop_b_sprinklered',
          text: 'Sprinklered?',
          section: 'property',
          sort_order: 90,
          field_type: 'boolean',
          required: true,
          field_path: 'sprinklered',
          conditions: [],
        },
        {
          id: 'prop_b_alarm',
          text: 'Central alarm?',
          section: 'property',
          sort_order: 100,
          field_type: 'boolean',
          required: true,
          field_path: 'central_alarm',
          conditions: [],
        },
        {
          id: 'prop_b_rcv',
          text: 'Building RCV',
          section: 'property',
          sort_order: 110,
          field_type: 'currency',
          placeholder: '3200000',
          required: true,
          field_path: 'building_rcv',
          conditions: [],
        },
        {
          id: 'prop_b_bpp',
          text: 'BPP / contents RCV',
          section: 'property',
          sort_order: 120,
          field_type: 'currency',
          placeholder: '150000',
          required: true,
          field_path: 'bpp_rcv',
          conditions: [],
        },
        {
          id: 'prop_b_bi',
          text: 'Business income (12-month revenue)',
          section: 'property',
          sort_order: 130,
          field_type: 'currency',
          placeholder: '450000',
          required: true,
          field_path: 'business_income',
          conditions: [],
        },
      ],
    },
    tags: ['property', 'lob', 'repeating'],
  },
];
